/**
 * InstallAppDialog — Step-by-step PWA install instructions modal.
 * Used as a fallback when the native install prompt is not available
 * (iOS Safari, non-Chromium browsers, or after the prompt was dismissed).
 *
 * Props:
 *   open     {boolean}           — whether the dialog is open
 *   onClose  {() => void}        — close handler
 *   variant  {'ios'|'fallback'}  — instruction set to show
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close,
  IosShare,
  AddBox,
  MoreVert,
  PhoneIphone,
  Check,
} from '@mui/icons-material';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../theme/tokens';

// Step definitions per variant
const IOS_STEPS = [
  {
    icon: <IosShare sx={{ color: 'white' }} />,
    primary: 'Tap the Share icon',
    secondary: "Look for the share button (box with an arrow) in Safari's toolbar at the bottom or top of the screen.",
  },
  {
    icon: <AddBox sx={{ color: 'white' }} />,
    primary: 'Tap "Add to Home Screen"',
    secondary: 'Scroll down in the share menu until you see "Add to Home Screen" and tap it.',
  },
  {
    icon: <Check sx={{ color: 'white' }} />,
    primary: 'Tap "Add"',
    secondary: 'Confirm by tapping "Add" in the top-right corner. The app icon will appear on your home screen.',
  },
];

const FALLBACK_STEPS = [
  {
    icon: <MoreVert sx={{ color: 'white' }} />,
    primary: 'Open the browser menu',
    secondary: 'Tap the three-dot menu (or the settings icon) in your browser toolbar.',
  },
  {
    icon: <PhoneIphone sx={{ color: 'white' }} />,
    primary: 'Tap "Install app" or "Add to Home Screen"',
    secondary: 'Look for an option labelled "Install app", "Add to Home Screen", or similar. This option may not appear in all browsers.',
  },
  {
    icon: <Check sx={{ color: 'white' }} />,
    primary: 'Confirm the install',
    secondary: 'Follow the on-screen prompt to complete the installation.',
  },
];

function StepNumber({ n }) {
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: GRADIENTS.brand,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="white" lineHeight={1}>
        {n}
      </Typography>
    </Box>
  );
}

StepNumber.propTypes = {
  n: PropTypes.number.isRequired,
};

function InstallAppDialog({ open, onClose, variant }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isIOS = variant === 'ios';
  const steps = isIOS ? IOS_STEPS : FALLBACK_STEPS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : RADIUS.xl,
          overflow: 'hidden',
        },
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          background: GRADIENTS.brand,
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="white"
          flex={1}
          component="div"
        >
          Install BookMyLibrary
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close install dialog"
          sx={{ color: 'rgba(255,255,255,0.85)' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogTitle sx={{ pb: 0, pt: 2, px: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          {isIOS
            ? 'Follow these steps in Safari to add BookMyLibrary to your home screen.'
            : 'Use your browser menu to install BookMyLibrary for quick access.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 1 }}>
        <List disablePadding>
          {steps.map((step, idx) => (
            <ListItem
              key={idx}
              alignItems="flex-start"
              sx={{ px: 0.5, py: 1.5, gap: 1.5 }}
              disableGutters
            >
              <ListItemIcon sx={{ minWidth: 'auto', mt: 0.25 }}>
                <StepNumber n={idx + 1} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {step.primary}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {step.secondary}
                  </Typography>
                }
                disableTypography
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            background: GRADIENTS.brand,
            color: 'white',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: RADIUS.lg,
            '&:hover': {
              background: GRADIENTS.brandDeep,
            },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

InstallAppDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['ios', 'fallback']).isRequired,
};

export default InstallAppDialog;
